import { useCallback, useEffect, useState } from 'react'

/**
 * Tema claro/oscuro, igual que en el resto de los sistemas.
 *
 * El Design System marca el tema en `<html data-theme="light|dark">`; Bootstrap
 * 5.3 usa su propio `data-bs-theme`, así que se mantienen los dos en sincronía.
 * La elección se guarda en `localStorage.theme` con la misma llave que usan
 * Proyectos, Operaciones y los demás.
 *
 * El tema se aplica ANTES de pintar desde un script en index.html: si se dejara
 * para cuando React monta, al entrar en modo oscuro se vería un destello blanco.
 *
 * @returns {{dark: boolean, theme: 'light'|'dark', toggle: () => void, set: (t: 'light'|'dark') => void}}
 */
export default function useTheme() {
  const [theme, setThemeState] = useState(() => {
    const actual = document.documentElement.getAttribute('data-theme')
    if (actual === 'dark' || actual === 'light') return actual
    try {
      const guardado = localStorage.getItem('theme')
      if (guardado === 'dark' || guardado === 'light') return guardado
    } catch {
      /* navegador sin almacenamiento (modo privado): manda el sistema */
    }
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
  })

  useEffect(() => {
    const root = document.documentElement
    root.setAttribute('data-theme', theme)
    root.setAttribute('data-bs-theme', theme)
    try {
      localStorage.setItem('theme', theme)
    } catch {
      /* si no se puede guardar, el tema igual vale para esta sesión */
    }
  }, [theme])

  // Si el usuario nunca eligió, se sigue al sistema operativo en vivo.
  useEffect(() => {
    let eligio = false
    try {
      eligio = !!localStorage.getItem('theme')
    } catch {
      eligio = false
    }
    if (eligio) return

    const mq = window.matchMedia?.('(prefers-color-scheme: dark)')
    if (!mq) return
    const alCambiar = (e) => setThemeState(e.matches ? 'dark' : 'light')
    mq.addEventListener('change', alCambiar)
    return () => mq.removeEventListener('change', alCambiar)
  }, [])

  const set = useCallback((t) => setThemeState(t === 'dark' ? 'dark' : 'light'), [])
  const toggle = useCallback(
    () => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')),
    []
  )

  return { dark: theme === 'dark', theme, toggle, set }
}
